import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, RotateCcw, Pause, Square, Check, AlertCircle, Volume2, Settings, XCircle, ChevronDown, RotateCw } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useAudioData } from './AudioContext';
import { uploadAudioBuzzBatch } from '../../services/apiService';
import JSZip from 'jszip';

const AudioBuzzDetection = () => {
  const navigate = useNavigate();
  
  // State from second component
  const [progress, setProgress] = useState(0);
  const [realProgress, setRealProgress] = useState(0);
  const [processedFiles, setProcessedFiles] = useState(0);
  const [filesRecorded, setFilesRecorded] = useState(0);
  const [filesConverted, setFilesConverted] = useState(0);
  const [filesAnalyzed, setFilesAnalyzed] = useState(0);
  const [totalProgress, setTotalProgress] = useState(0);

  // Phase weights (must sum to 1.0)
  const WEIGHT_RECORDING = 0.30;   // 30% - Sequential playback + recording
  const WEIGHT_CONVERSION = 0.10;  // 10% - WebM → WAV conversion
  const WEIGHT_API = 0.60;         // 60% - API analysis (bottleneck)

  // ETA tracking
  const [etaSeconds, setEtaSeconds] = useState(null);
  const [etaMessage, setEtaMessage] = useState('');
  const etaIntervalRef = useRef(null);
  const firstBatchStartTimeRef = useRef(null);
  const hasEstimatedEtaRef = useRef(false);
  const batchTimingsRef = useRef([]); // For moving average

  // Setconfiguration
  const [inputOpen, setInputOpen] = useState(false);
  const [outputOpen, setOutputOpen] = useState(false);
  const inputRef = useRef(null);
  const outputRef = useRef(null);

  // MIC
  const micCleanedUpRef = useRef(false);

  // Basic-frequency
  const [shouldResetFrequency, setShouldResetFrequency] = useState(false);

  // Status messages
  const [statusMessage, setStatusMessage] = useState('');
  const [currentPhase, setCurrentPhase] = useState('idle'); // 'idle', 'recording', 'converting', 'analyzing', 'complete'
  const [messageIndex, setMessageIndex] = useState(0);
  const messageIntervalRef = useRef(null);

  // Core state
  const [currentFreq, setCurrentFreq] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const [detectionResult, setDetectionResult] = useState('');
  const [totalFiles, setTotalFiles] = useState(0);
  const [systemStatus, setSystemStatus] = useState('Ready');
  const [logs, setLogs] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [recordedAudioData, setRecordedAudioData] = useState([]);
  const [apiResults, setApiResults] = useState([]);
  const [showAllBuzz, setShowAllBuzz] = useState(false);
  const [hasStartedDetection, setHasStartedDetection] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [loadedViaDefaults, setLoadedViaDefaults] = useState(false);

  const { calibrationData } = useAudioData();
  const fileInputRef = useRef(null);
  const [syntheticFileList, setSyntheticFileList] = useState(null);

  const { setDetectionResults, setOriginalAudioFiles, setHumanJudgment, humanJudgment, setHasBuzzDetected, detectionResults, originalAudioFiles, hasBuzzDetected } = useAudioData();

  // ✅ Professional Technical Messages
  const engagingMessages = [
    "🔊 Extracting audio features...",
    "📊 Computing spectral analysis...",
    "🎵 Processing waveform data...",
    "⚡ Running FFT transformations...",
    "🎧 Analyzing frequency domains...",
    "📈 Calculating amplitude metrics...",
    "🔍 Detecting audio anomalies...",
    "⚙️ Normalizing signal levels...",
    "🎼 Extracting MFCC coefficients...",
    "💾 Encoding audio streams...",
    "🧮 Computing temporal features...",
    "🔬 Analyzing spectrograms...",
    "📉 Measuring noise thresholds...",
    "⏱️ Processing time-domain data...",
    "🎚️ Applying signal filters...",
    "🔊 Evaluating audio quality metrics...",
    "📊 Running model inference...",
    "🎵 Segmenting audio chunks...",
    "🔍 Validating data integrity...",
    "⚡ Optimizing buffer processing...",
    "📈 Extracting RMS values...",
    "🎧 Computing zero-crossing rates...",
    "💻 Serializing audio metadata...",
    "🔬 Performing outlier detection...",
    "📊 Calculating statistical variance..."
  ];

  // Device configuration
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [selectedInput, setSelectedInput] = useState('');
  const [selectedOutput, setSelectedOutput] = useState('');
  const [microphoneStatus, setMicrophoneStatus] = useState('disconnected');
  const [batchSize, setBatchSize] = useState(3);
  const [canControlRecording, setCanControlRecording] = useState(false);
  
  // Analysis Parameters
  const [strongThreshold, setStrongThreshold] = useState(-45);
  const [stdThreshold1, setStdThreshold1] = useState(15);
  const [stdThreshold2, setStdThreshold2] = useState(14);
  const [outlierCountThreshold, setOutlierCountThreshold] = useState(7);
  

  // Refs
  const playerRef = useRef(null);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const abortControllerRef = useRef(null);
  // MIC
  const originalMicStreamRef = useRef(null);      // ← NEW: keep the raw gUM stream here
  const sourceRef = useRef(null);                 // ← NEW: keep reference to source node
  const gainNodeRef = useRef(null);

  // ✅ Helper function to extract base frequency from filename
  function extractBaseFrequency(filename) {
    // Match number followed by Hz / HZ / hz
    const match = filename.match(/(\d+)\s*hz/i);
    return match ? Number(match[1]) : null;
  }

  // Calculate weighted progress from all three phases
  const calculateWeightedProgress = (recorded, converted, analyzed, total) => {
    if (total === 0) return 0;

    const recordingProgress = (recorded / total) * WEIGHT_RECORDING;
    const conversionProgress = (converted / total) * WEIGHT_CONVERSION;
    const apiProgress = (analyzed / total) * WEIGHT_API;

    const totalWeightedProgress = (recordingProgress + conversionProgress + apiProgress) * 100;

    return Math.min(100, Math.max(0, totalWeightedProgress));
  };

  // Update total progress based on current phase completions
  const updateProgress = () => {
    const progress = calculateWeightedProgress(
      filesRecorded,
      filesConverted,
      filesAnalyzed,
      totalFiles
    );
    setTotalProgress(Math.round(progress));
  };

  // Format ETA time
  const formatTime = (sec) => {
    if (!sec || sec <= 0) return null;
    if (sec < 60) return `${sec}s remaining`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s > 0 ? `${m}m ${s}s remaining` : `${m}m remaining`;
  };

  // Initialize ETA after first batch completes
  const initializeETA = (batchDurationMs, batchSize, totalFilesCount, completedCount) => {
    if (batchSize === 0 || batchDurationMs <= 0) return;

    const perFileMs = batchDurationMs / batchSize;
    const remainingFiles = totalFilesCount - completedCount;

    if (remainingFiles > 0) {
      const estimatedRemainingMs = perFileMs * remainingFiles;
      const estimatedSeconds = Math.round(estimatedRemainingMs / 1000);

      batchTimingsRef.current.push(perFileMs);
      setEtaSeconds(estimatedSeconds);
      setEtaMessage(formatTime(estimatedSeconds));

      // Start countdown
      startEtaCountdown();

      hasEstimatedEtaRef.current = true;
      addLog(`⏱️ ETA initialized: ${formatTime(estimatedSeconds)}`);
    }
  };

  // Update ETA with moving average after each batch
  const updateETA = (batchDurationMs, batchSize, totalFilesCount, completedCount) => {
    if (batchSize === 0 || batchDurationMs <= 0) return;

    const perFileMs = batchDurationMs / batchSize;
    batchTimingsRef.current.push(perFileMs);

    // Use moving average of last 3 batches for smoother ETA
    const recentTimings = batchTimingsRef.current.slice(-3);
    const avgPerFileMs = recentTimings.reduce((sum, t) => sum + t, 0) / recentTimings.length;

    const remainingFiles = totalFilesCount - completedCount;

    if (remainingFiles > 0) {
      const estimatedRemainingMs = avgPerFileMs * remainingFiles;
      const estimatedSeconds = Math.round(estimatedRemainingMs / 1000);

      setEtaSeconds(estimatedSeconds);
      setEtaMessage(formatTime(estimatedSeconds));
    } else {
      // All files completed
      clearETA();
    }
  };

  // Start ETA countdown
  const startEtaCountdown = () => {
    clearInterval(etaIntervalRef.current);
    etaIntervalRef.current = setInterval(() => {
      setEtaSeconds(prev => {
        if (prev === null || prev <= 0) return null;
        const newVal = prev - 1;
        setEtaMessage(formatTime(newVal));
        return newVal;
      });
    }, 1000);
  };

  // ✅ Stop ETA countdown
  const stopEtaCountdown = () => {
    clearInterval(etaIntervalRef.current);
  };

  // Clear ETA completely
  const clearETA = () => {
    stopEtaCountdown();
    setEtaSeconds(null);
    setEtaMessage('');
    hasEstimatedEtaRef.current = false;
    firstBatchStartTimeRef.current = null;
    batchTimingsRef.current = [];
  };

  // ✅ Rotate messages
  const rotateMessage = () => {
    setMessageIndex(prev => (prev + 1) % engagingMessages.length);
  };

  // ✅ Start message rotation
  const startMessageRotation = () => {
    setMessageIndex(0);
    setStatusMessage(engagingMessages[0]);
    clearInterval(messageIntervalRef.current);
    messageIntervalRef.current = setInterval(rotateMessage, 3000);
  };

  // ✅ Stop message rotation
  const stopMessageRotation = () => {
    clearInterval(messageIntervalRef.current);
    setStatusMessage("🎉 All done! Your audios are ready!");
  };

  // Auto-update progress when phase counters change
  useEffect(() => {
    updateProgress();
  }, [filesRecorded, filesConverted, filesAnalyzed, totalFiles]);

  // ✅ Update status message when index changes
  useEffect(() => {
    if (isRunning && !isPaused) {
      setStatusMessage(engagingMessages[messageIndex]);
    }
  }, [messageIndex, isRunning, isPaused]);

   useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputOpen && inputRef.current && !inputRef.current.contains(event.target)) {
        setInputOpen(false);
      }
      if (outputOpen && outputRef.current && !outputRef.current.contains(event.target)) {
        setOutputOpen(false);
      }
    };

    if (inputOpen || outputOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputOpen, outputOpen]);

  // ✅ Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      stopEtaCountdown();
      stopMessageRotation();
    };
  }, []);

  useEffect(() => {
    getDevices();
    return () => cleanupAll();
  }, []);

  // Handle pre-loaded detection results
  useEffect(() => {
    if (detectionResults && detectionResults.length > 0) {
      setApiResults(detectionResults);
      setUploadedFiles(originalAudioFiles ? originalAudioFiles.map(o => o.file) : []);
      setRecordedAudioData([]);

      // Set to 100% complete
      const count = detectionResults.length;
      setFilesRecorded(count);
      setFilesConverted(count);
      setFilesAnalyzed(count);
      setTotalProgress(100);
      setTotalFiles(count);

      setDetectionResult(hasBuzzDetected ? 'fail' : 'pass');
      setSystemStatus('Ready');
      setIsRunning(false);
      setIsPaused(false);
      setCurrentPhase('complete');
      clearETA();
    }
  }, [detectionResults, originalAudioFiles, hasBuzzDetected]);

  // Update total files when uploaded files change
  useEffect(() => {
    setTotalFiles(uploadedFiles.length);
  }, [uploadedFiles]);

  // Sync pause state to ref
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
  if (sessionStorage.getItem('justSubmittedAudioBuzz') === 'true') {
    // Reset everything
    setDetectionResults([]);
    setOriginalAudioFiles([]);
    setApiResults([]);
    setUploadedFiles([]);
    setHasBuzzDetected(false);
    setHumanJudgment(null);
    setProgress(0);
    setRealProgress(0);
    setProcessedFiles(0);
    setTotalFiles(0);
    setDetectionResult('');
    setSystemStatus('Ready');
    setIsRunning(false);
    setIsPaused(false);
    // setIsAnimating(false);
    sessionStorage.removeItem('justSubmittedAudioBuzz');
  }
}, []); // important: only once on mount

useEffect(() => {
  if (shouldResetFrequency) {
    setCurrentFreq(null);
    setShouldResetFrequency(false);
    console.log('Fresh upload detected → forced currentFreq = null');
    return; 
  }

  if (
    originalAudioFiles?.length > 0 &&
    currentFreq === null &&
    !isRunning 
  ) {
    const lastFreq = originalAudioFiles.at(-1)?.frequency;
    if (lastFreq) {
      setCurrentFreq(lastFreq);
      addLog(`✓ Restored last frequency: ${lastFreq} Hz after navigation`);
    }
  }
}, [originalAudioFiles, currentFreq, shouldResetFrequency, isRunning]);

  // ===== DEVICE MANAGEMENT =====
  const getDevices = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => stream.getTracks().forEach(track => track.stop()))
        .catch(() => { });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(device => device.kind === 'audioinput');
      const outputs = devices.filter(device => device.kind === 'audiooutput');
      setAudioInputs(inputs);
      setAudioOutputs(outputs);
      if (inputs.length > 0 && !selectedInput) setSelectedInput(inputs[0].deviceId);
      if (outputs.length > 0 && !selectedOutput) setSelectedOutput(outputs[0].deviceId);
    } catch (err) {
      console.error('Error enumerating devices:', err);
    }
  };

  const initMicrophone = async () => {
  try {
    // Audacity-equivalent configuration
    const AUDACITY_CONFIG = {
      sampleRate: 44100,           // 44100 Hz
      sampleFormat: '32-bit float', // 32-bit float (Audacity default)
      channelCount: 1,              // Mono
      recordingLevel: 0.5,          // 50% gain
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    };

    addLog('═══════════════════════════════════════════════════════');
    addLog('📋 AUDACITY-EQUIVALENT RECORDING CONFIGURATION');
    addLog('═══════════════════════════════════════════════════════');
    addLog(`✓ Sample Rate: ${AUDACITY_CONFIG.sampleRate} Hz`);
    addLog(`✓ Sample Format: ${AUDACITY_CONFIG.sampleFormat}`);
    addLog(`✓ Recording Channels: ${AUDACITY_CONFIG.channelCount} (Mono)`);
    addLog(`✓ Recording Level: ${AUDACITY_CONFIG.recordingLevel * 100}%`);
    addLog(`✓ Echo Cancellation: ${AUDACITY_CONFIG.echoCancellation}`);
    addLog(`✓ Noise Suppression: ${AUDACITY_CONFIG.noiseSuppression}`);
    addLog(`✓ Auto Gain Control: ${AUDACITY_CONFIG.autoGainControl}`);
    addLog('═══════════════════════════════════════════════════════');

    const constraints = {
      audio: {
        deviceId: selectedInput ? { exact: selectedInput } : undefined,
        echoCancellation: AUDACITY_CONFIG.echoCancellation,
        noiseSuppression: AUDACITY_CONFIG.noiseSuppression,
        autoGainControl: AUDACITY_CONFIG.autoGainControl,
        sampleRate: AUDACITY_CONFIG.sampleRate,
        channelCount: AUDACITY_CONFIG.channelCount,
      },
    };

    addLog('🔄 Requesting microphone access...');
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    
    originalMicStreamRef.current = stream; 
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0 || !audioTracks[0].enabled) {
      throw new Error('Microphone tracks not available or disabled');
    }

    // Create AudioContext with Audacity sample rate
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: AUDACITY_CONFIG.sampleRate,
      });

      addLog('═══════════════════════════════════════════════════════');
      addLog('🎛️  AUDIO CONTEXT CONFIGURATION');
      addLog('═══════════════════════════════════════════════════════');
      addLog(`✓ Audio Context Sample Rate: ${audioContextRef.current.sampleRate} Hz`);
      addLog(`✓ Audio Context State: ${audioContextRef.current.state}`);
      addLog(`✓ Internal Processing: 32-bit float (Web Audio API)`);
      addLog('═══════════════════════════════════════════════════════');
    }

    // Create audio processing pipeline: source → gain → destination
    const source = audioContextRef.current.createMediaStreamSource(stream);
    const gainNode = audioContextRef.current.createGain();
    gainNode.gain.value = AUDACITY_CONFIG.recordingLevel; // 50% gain

    const destination = audioContextRef.current.createMediaStreamDestination();

    source.connect(gainNode);
    gainNode.connect(destination);

    sourceRef.current = source;
    gainNodeRef.current = gainNode;
    micStreamRef.current = destination.stream;

    addLog('═══════════════════════════════════════════════════════');
    addLog('🎚️  SOFTWARE GAIN CONTROL (50%)');
    addLog('═══════════════════════════════════════════════════════');
    addLog(`✓ Gain Node: ${AUDACITY_CONFIG.recordingLevel * 100}%`);
    addLog(`✓ Pipeline: MediaStream → GainNode(0.5) → Destination`);
    addLog('═══════════════════════════════════════════════════════');

    setMicrophoneStatus('connected');
    addLog('✅ MICROPHONE INITIALIZATION COMPLETE');
    return true;
  } catch (error) {
    console.error('Microphone init error:', error);
    addLog('❌ MICROPHONE INITIALIZATION FAILED');
    addLog(`✗ Error: ${error.message}`);
    if (originalMicStreamRef.current) {
      originalMicStreamRef.current.getTracks().forEach(t => t.stop());
      originalMicStreamRef.current = null;
    }
    setMicrophoneStatus('error');
    return false;
  }
};

  const setAudioOutput = async (deviceId) => {
    if (!deviceId || !playerRef.current) return;
    
    try {
      if (typeof playerRef.current.setSinkId !== 'undefined') {
        await playerRef.current.setSinkId(deviceId);
      }
    } catch (err) {
      console.error('Error setting audio output:', err);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || syntheticFileList || []);

    if (!files.length) {
      addLog('✗ No valid audio files found');
      return;
    }

    const audioFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      const isAudioMimeType = file.type.startsWith('audio/');
      const isAudioExtension = ['wav', 'mp3', 'ogg', 'webm', 'm4a', 'aac'].includes(extension);
      return isAudioMimeType || isAudioExtension;
    });

    if (!audioFiles.length) {
      addLog('✗ No valid audio files found');
      return;
    }

    audioFiles.sort((a, b) => a.name.localeCompare(b.name));

    // Reset all progress and state
    setFilesRecorded(0);
    setFilesConverted(0);
    setFilesAnalyzed(0);
    setTotalProgress(0);
    setIsPaused(false);
    setIsRunning(false);
    setCurrentPhase('idle');
    setDetectionResult(null);
    setSystemStatus('Idle');
    setHumanJudgment(null);
    setRecordedAudioData([]);
    setApiResults([]);
    clearETA();
    setStatusMessage('');
    setOriginalAudioFiles([]);
    setDetectionResults([]);
    setHasBuzzDetected(false);
    setShouldResetFrequency(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setUploadedFiles(audioFiles);

    addLog(`✓ Verifying all ${audioFiles.length} audio files...`);
    setTimeout(() => {
      addLog(`✓ All ${audioFiles.length} audio files verified`);
    }, 500);
  };

  const handleHumanJudgment = (judgment) => {
    setHumanJudgment((prev) => {
      const newJudgment = prev === judgment ? null : judgment;

      if (newJudgment) {
        addLog(
          `👤 Human judgment: ${
            newJudgment === 'no_buzz' ? 'No Buzz' : 'Audio Buzz'
          }`
        );
        console.log('Human Judgment:', newJudgment);

        if (apiResults.length > 0) {
          const aiBuzzDetected = apiResults.some(
            (r) => r.success && r.result?.prediction === 'Buzz'
          );
          const humanBuzzDetected = newJudgment === 'buzz';

          if (aiBuzzDetected === humanBuzzDetected) {
            addLog('✓ Human judgment matches AI detection');
          } else {
            addLog('⚠ Human judgment differs from AI detection');
          }
        }
      } else {
        addLog('👤 Human judgment cleared');
        console.log('Human Judgment cleared');
      }

      return newJudgment;
    });
  };

  const playAudioFile = (file, shouldStop) => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      
      if (!playerRef.current) {
        playerRef.current = new Audio();
      }

      playerRef.current.src = url;
      playerRef.current.load();

      const checkInterval = setInterval(() => {
        if (shouldStop()) {
          clearInterval(checkInterval);
          if (playerRef.current && !playerRef.current.paused) {
            playerRef.current.pause();
            playerRef.current.currentTime = 0;
          }
          URL.revokeObjectURL(url);
          resolve('paused');
        }
      }, 50);

      playerRef.current.oncanplaythrough = () => {
        if (!shouldStop()) {
          playerRef.current.play().catch((err) => {
            clearInterval(checkInterval);
            reject(err);
          });
        } else {
          clearInterval(checkInterval);
          URL.revokeObjectURL(url);
          resolve('paused');
        }
      };

      playerRef.current.onended = () => {
        clearInterval(checkInterval);
        URL.revokeObjectURL(url);
        resolve('completed');
      };

      playerRef.current.onerror = (e) => {
        clearInterval(checkInterval);
        URL.revokeObjectURL(url);
        reject(new Error(`Failed to play ${file.name}`));
      };
    });
  };

  const startRecording = () => {
  if (!micStreamRef.current) {
    addLog('✗ Microphone stream not available');
    return false;
  }

  const tracks = micStreamRef.current.getAudioTracks();
  if (tracks.length === 0 || !tracks[0].enabled || tracks[0].readyState !== 'live') {
    addLog('✗ Microphone track not active');
    return false;
  }

  audioChunksRef.current = [];

  try {
    // Use WebM with Opus codec (standard for Chrome/Edge)
    const mimeType = 'audio/webm;codecs=opus';
    
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      addLog('⚠ WebM/Opus not supported, using browser default');
      mediaRecorderRef.current = new MediaRecorder(micStreamRef.current, {
        audioBitsPerSecond: 256000, // Higher bitrate for better quality
      });
    } else {
      mediaRecorderRef.current = new MediaRecorder(micStreamRef.current, {
        mimeType: mimeType,
        audioBitsPerSecond: 256000, // Increased from 128000
      });
    }

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onerror = (event) => {
      console.error('MediaRecorder error:', event.error);
      addLog(`✗ Recording error: ${event.error}`);
    };

    // Record in 100ms chunks for better quality preservation
    mediaRecorderRef.current.start(100);
    addLog('🔴 Recording started (Audacity-equivalent settings)');
    return true;
  } catch (error) {
    console.error('Recording start error:', error);
    addLog(`✗ Failed to start recording: ${error.message}`);
    return false;
  }
};

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
        resolve(null);
        return;
      }
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        resolve(audioBlob);
      };
      
      mediaRecorderRef.current.stop();
    });
  };

  const audioBufferToWav32Float = (audioBuffer) => {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 3; // IEEE float
  const bitDepth = 32; // 32-bit

  addLog('🔧 WAV Encoding (Audacity-equivalent):');
  addLog(`   • Format: IEEE Float (${format})`);
  addLog(`   • Bit Depth: ${bitDepth}-bit`);
  addLog(`   • Sample Rate: ${sampleRate} Hz`);
  addLog(`   • Channels: ${numberOfChannels}`);

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numberOfChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = audioBuffer.length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // WAV Header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true); // IEEE float = 3
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Write 32-bit float samples (NO conversion, preserving original quality)
  let offset = 44;
  for (let i = 0; i < audioBuffer.length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = audioBuffer.getChannelData(channel)[i];
      view.setFloat32(offset, sample, true); // Write as 32-bit float
      offset += 4;
    }
  }

  addLog(`✓ 32-bit float WAV buffer created: ${(buffer.byteLength / 1024).toFixed(2)} KB`);
  addLog('✓ Audio quality preserved (no bit depth conversion)');

  return buffer;
};



  const convertWebMToWAV = async (webmBlob) => {
  try {
    addLog('═══════════════════════════════════════════════════════');
    addLog('🔄 AUDIO FORMAT CONVERSION (WebM → 32-bit float WAV)');
    addLog('═══════════════════════════════════════════════════════');
    addLog(`📥 Input: WebM blob (${(webmBlob.size / 1024).toFixed(2)} KB)`);

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 44100,
      });
    }

    const arrayBuffer = await webmBlob.arrayBuffer();
    addLog(`✓ ArrayBuffer created: ${(arrayBuffer.byteLength / 1024).toFixed(2)} KB`);

    const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

    addLog('📊 Decoded Audio Buffer:');
    addLog(`   • Sample Rate: ${audioBuffer.sampleRate} Hz`);
    addLog(`   • Channels: ${audioBuffer.numberOfChannels}`);
    addLog(`   • Duration: ${audioBuffer.duration.toFixed(2)} seconds`);
    addLog(`   • Length: ${audioBuffer.length} samples`);

    // Convert to 32-bit float WAV (matching Audacity)
    const wavBuffer = audioBufferToWav32Float(audioBuffer);
    const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });

    addLog(`📤 Output: 32-bit float WAV (${(wavBlob.size / 1024).toFixed(2)} KB)`);
    addLog('✅ Conversion complete: WebM → 32-bit float PCM WAV');
    addLog('═══════════════════════════════════════════════════════');

    return wavBlob;
  } catch (error) {
    addLog('❌ WAV CONVERSION FAILED');
    addLog(`✗ Error: ${error.message}`);
    throw error;
  }
};

  const audioBufferToWav = (audioBuffer) => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16; // 16-bit output

    addLog('🔧 WAV Encoding Parameters:');
    addLog(`   • Format: PCM (${format})`);
    addLog(`   • Bit Depth: ${bitDepth}-bit`);
    addLog(`   • Sample Rate: ${sampleRate} Hz`);
    addLog(`   • Channels: ${numberOfChannels}`);
    addLog(`   • Conversion: 32-bit float → ${bitDepth}-bit PCM`);

    let length = audioBuffer.length * numberOfChannels * 2;
    let buffer = new ArrayBuffer(44 + length);
    let view = new DataView(buffer);

    // WAV header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * bitDepth / 8, true);
    view.setUint16(32, numberOfChannels * bitDepth / 8, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, length, true);

    // Convert 32-bit float samples to 16-bit PCM
    let offset = 44;
    let clippedSamples = 0;
    for (let i = 0; i < audioBuffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        let sample = audioBuffer.getChannelData(channel)[i];

        // Track clipping
        if (sample > 1 || sample < -1) {
          clippedSamples++;
        }

        // Clamp to [-1, 1] range
        sample = Math.max(-1, Math.min(1, sample));

        // Convert to 16-bit integer
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, sample, true);
        offset += 2;
      }
    }

    if (clippedSamples > 0) {
      const totalSamples = audioBuffer.length * numberOfChannels;
      const clippingPercentage = ((clippedSamples / totalSamples) * 100).toFixed(2);
      addLog(`⚠️  Clipping detected: ${clippedSamples}/${totalSamples} samples (${clippingPercentage}%)`);
    } else {
      addLog('✓ No clipping detected in conversion');
    }

    addLog(`✓ WAV buffer created: ${(buffer.byteLength / 1024).toFixed(2)} KB`);

    return buffer;
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  const sendBatchToAPI = async (recordings) => {
    try {
      const formData = new FormData();
      
      recordings.forEach((recording) => {
        formData.append('audioFiles', recording.wavBlob, recording.fileName.replace(/\.[^/.]+$/, '.wav'));
        if (recording.originalFile) {
          formData.append('originalAudios', recording.originalFile, recording.originalFile.name);
        }
      });

      formData.append('threshold_strong', strongThreshold);
      formData.append('std_threshold_1', stdThreshold1);
      formData.append('std_threshold_2', stdThreshold2);
      formData.append('outlier_count_threshold', outlierCountThreshold);
      formData.append('audio_interface', calibrationData.audioInterfaceModel);

      const data = await uploadAudioBuzzBatch(formData);
      
      const results = data.results.map((apiResult, index) => ({
        fileName: recordings[index].fileName,
        frequency: recordings[index].frequency,
        success: apiResult.success,
        result: apiResult.result,
        wavBlob: recordings[index].wavBlob,
        originalFile: recordings[index].originalFile,
      }));

      return results;
    } catch (error) {
      addLog(`✗ Batch API error: ${error.message}`);
      return recordings.map((recording) => ({
        fileName: recording.fileName,
        frequency: recording.frequency,
        success: false,
        error: error.message,
      }));
    }
  };

 const startDetection = async () => {
  setHasStartedDetection(true);
  if (uploadedFiles.length === 0) {
    addLog('✗ Please upload audio files first');
    return;
  }

  try {
    // Mic init with error handling
    if (microphoneStatus !== 'connected') {
      const success = await initMicrophone();
      if (!success) {
        addLog('✗ Microphone initialization failed - retry or check permissions');
        setCanControlRecording(false); 
        return;
      }
    }

    // Output set with error handling
    if (selectedOutput) {
      try {
        await setAudioOutput(selectedOutput);
      } catch (outputError) {
        console.error('Audio output set failed:', outputError);
        addLog(' Audio output setup warning - continuing without custom output');
        
      }
    }

      setIsRunning(true);
      setIsPaused(false);
      setCanControlRecording(true);

      // Reset progress tracking
      setFilesRecorded(0);
      setFilesConverted(0);
      setFilesAnalyzed(0);
      setTotalProgress(0);
      setCurrentPhase('recording');

      setDetectionResult('pending');
      setSystemStatus('Processing');
      setRecordedAudioData([]);
      setApiResults([]);
      setHumanJudgment(null);

      // Clear and prepare ETA
      clearETA();

      // Start status messages
      startMessageRotation();

      abortControllerRef.current = new AbortController();
      addLog('🚀 Starting detection...');

      await processAllFiles();
      cleanupAll();
    } catch (error) {
      cleanupAll();
      console.error('Detection startup failed:', error);
      addLog(`✗ Startup error: ${error.message} - Controls disabled`);
      setIsRunning(false);
      setCanControlRecording(false);
      setSystemStatus('Error');
      setCurrentPhase('idle');
      clearETA();
    }
  };

  const processAllFiles = async () => {
    const allRecordings = [];
    const allApiResults = [];

    // Progress already initialized in startDetection
    // Just reset ETA tracking
    firstBatchStartTimeRef.current = null;
    hasEstimatedEtaRef.current = false;
    const total = uploadedFiles.length;
    const batches = chunkArray(uploadedFiles, batchSize);
    
    addLog(`📦 Processing ${uploadedFiles.length} files in ${batches.length} batches`);
    
    // ✅ API processing queue - runs independently
    let apiQueue = [];
    let apiCompletedCount = 0;
    let isProcessingApi = false;

    const processApiQueueInBackground = async () => {
      if (isProcessingApi || apiQueue.length === 0) return;
      isProcessingApi = true;

      while (apiQueue.length > 0) {
        // ✅ Wait if paused
        while (isPausedRef.current) {
          await sleep(500);
        }

        const batchRecordings = apiQueue.shift();
        if (!batchRecordings || batchRecordings.length === 0) {
          continue;
        }

        addLog(`📤 Sending batch to API (${batchRecordings.length} files)...`);

        // ✅ If this is the first batch being sent, note the start time
        if (firstBatchStartTimeRef.current === null) {
          firstBatchStartTimeRef.current = performance.now();
        }

        try {
          const before = performance.now();
          const batchResults = await sendBatchToAPI(batchRecordings);
          const after = performance.now();

          allApiResults.push(...batchResults);
          setApiResults([...allApiResults]);

          // ✅ UPDATE WEIGHTED PROGRESS - API phase complete for these files
          setFilesAnalyzed(prev => prev + batchResults.length);
          apiCompletedCount += batchResults.length;
          setCurrentPhase('analyzing');

          addLog(`✓ Batch API complete (${batchResults.length} results received)`);

          // ✅ ETA Management - Initialize or Update
          if (!hasEstimatedEtaRef.current) {
            const batchDurationMs = after - firstBatchStartTimeRef.current;
            initializeETA(batchDurationMs, batchResults.length, total, apiCompletedCount);
          } else {
            const batchDurationMs = after - before;
            updateETA(batchDurationMs, batchResults.length, total, apiCompletedCount);
          }
        } catch (error) {
          addLog(`✗ Batch API error: ${error.message}`);
          // Still mark as processed even if failed
          setFilesAnalyzed(prev => prev + batchRecordings.length);
          apiCompletedCount += batchRecordings.length;
        }
      }

      isProcessingApi = false;
    };

    // ✅ Start API processing in background (don't await)
    const apiProcessingPromise = (async () => {
      while (true) {
        await processApiQueueInBackground();
        if (apiQueue.length === 0 && allRecordings.length === total) {
          break;
        }
        await sleep(500);
      }
    })();

    // Record batches sequentially (in foreground)
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      addLog(`🔄 Recording batch ${batchIndex + 1}/${batches.length} (${batch.length} files)`);
      
      const batchRecordings = [];
      
      for (let fileIndex = 0; fileIndex < batch.length; fileIndex++) {
        const file = batch[fileIndex];
        
        // ✅ Auto-detect base frequency from filename using helper
        let frequency = extractBaseFrequency(file.name);

        // Fallback if not found
        if (!frequency || Number.isNaN(frequency)) {
          frequency = 60;
        }
        
        setCurrentFreq(frequency);
        
        let recordingCompleted = false;
        let wavBlob = null;
        let webmBlob = null;
        
        while (!recordingCompleted) {
          // ✅ PAUSE HANDLING - Wait while paused
          while (isPausedRef.current) {
            addLog(`⏸ Recording paused for ${file.name}`);
            await sleep(500);
          }
          
          // ✅ After resuming from pause, we restart recording from scratch
          addLog(`🔄 Starting/Restarting ${file.name} from beginning`);
          
          try {
            // ✅ Clear previous recording chunks (important for restart)
            audioChunksRef.current = [];
            
            // ✅ START RECORDING
            const recordingStarted = startRecording();
            if (!recordingStarted) {
              addLog(`✗ Failed to start recording for ${file.name}`);
              break;
            }
            
            addLog(`🔴 Recording started for ${file.name} at ${frequency}Hz...`);
            await sleep(200);
            
            // ✅ PLAY AUDIO WITH PAUSE CHECK
            addLog(`▶ Playing ${file.name}...`);
            try {
              // Pass a function that checks pause state
              const result = await playAudioFile(file, () => isPausedRef.current);
              
              // If paused during playback, stop recording and restart
              if (result === 'paused') {
                addLog(`⏸ Paused during playback of ${file.name}`);
                await stopRecording();
                continue; // Go back to while loop to wait for resume
              }
              
            } catch (playError) {
              addLog(`⚠ Playback error: ${playError.message}`);
              // If error occurred, check if it was due to pause
              if (isPausedRef.current) {
                await stopRecording();
                continue;
              }
            }
            
            // Continue recording after audio ends
            await sleep(500);
            
            // ✅ Double-check pause state before stopping recording
            if (isPausedRef.current) {
              addLog(`⏸ Paused after playback of ${file.name} - will restart on resume`);
              await stopRecording();
              continue;
            }
            
            // ✅ STOP RECORDING
            webmBlob = await stopRecording();
            
            if (!webmBlob || webmBlob.size === 0) {
              addLog(`✗ No audio recorded for ${file.name} (empty blob, size: 0 bytes)`);
              continue;
            }
            
            if (webmBlob.size < 5000) {
              addLog(`⚠ Warning: Very small recording (${(webmBlob.size / 1024).toFixed(2)}KB) - might be incomplete`);
            }
            
            addLog(`✓ ${file.name} recorded successfully (size: ${(webmBlob.size / 1024).toFixed(2)}KB)`);

            // ✅ UPDATE PROGRESS - RECORDING PHASE COMPLETE
            setFilesRecorded(prev => prev + 1);
            setCurrentPhase('converting');

            // Convert WebM to WAV
            try {
              wavBlob = await convertWebMToWAV(webmBlob);
              
              if (!wavBlob || wavBlob.size === 0) {
                addLog(`✗ WAV conversion failed for ${file.name}`);
                continue;
              }
              
              addLog(`✓ Converted to WAV (size: ${(wavBlob.size / 1024).toFixed(2)}KB)`);

              // ✅ UPDATE PROGRESS - CONVERSION PHASE COMPLETE
              setFilesConverted(prev => prev + 1);

              recordingCompleted = true;
              
            } catch (convError) {
              addLog(`✗ WAV conversion error for ${file.name}: ${convError.message}`);
              continue;
            }
            
          } catch (error) {
            addLog(`✗ Error processing ${file.name}: ${error.message}`);
            console.error('Recording error details:', error);
            break;
          }
        }
        
        // ✅ ONLY ADD IF SUCCESSFULLY COMPLETED
        if (recordingCompleted && wavBlob && webmBlob) {
          batchRecordings.push({
            fileName: file.name,
            webmBlob: webmBlob,
            wavBlob: wavBlob,
            originalFile: file,
            frequency: frequency
          });
          
          addLog(`📝 Added ${file.name} to batch for API processing`);
          
          // Wait before next recording
          await sleep(300);
        } else {
          addLog(`⚠ Skipping ${file.name} - recording failed`);
        }
      }
      
      if (batchRecordings.length === 0) {
        addLog(`⚠ Warning: No successful recordings in batch ${batchIndex + 1}`);
      } else {
        addLog(`✓ Batch ${batchIndex + 1}: ${batchRecordings.length} files ready for API`);
      }
      
      allRecordings.push(...batchRecordings);
      
      // ✅ QUEUE THIS BATCH FOR API PROCESSING (don't wait)
      apiQueue.push(batchRecordings);
      addLog(`📋 Queued batch ${batchIndex + 1} for API processing (${apiQueue.length} batches in queue)`);
      
      // Trigger API processing without blocking recording
      processApiQueueInBackground();
    }

    // ✅ All files have been recorded – disable Pause/Stop now
    setCanControlRecording(false);

    // ✅ Wait for all API calls to complete
    await apiProcessingPromise;
    
    // Final wait to ensure everything is done
    let finalWaitCounter = 0;
    const maxFinalWait = 30000; // 30 seconds final wait
    while ((isProcessingApi || apiQueue.length > 0) && finalWaitCounter < maxFinalWait) {
      await sleep(500);
      finalWaitCounter += 500;
      await processApiQueueInBackground();
    }
    
    if (finalWaitCounter >= maxFinalWait) {
      addLog(`⚠ Final API processing timeout reached`);
    }

    // ========== COMPLETION ==========
    // Ensure 100% complete
    setFilesRecorded(total);
    setFilesConverted(total);
    setFilesAnalyzed(total);
    setTotalProgress(100);
    setCurrentPhase('complete');

    setRecordedAudioData(allRecordings);
    setApiResults(allApiResults);

    // Stop ETA and messages
    stopEtaCountdown();
    stopMessageRotation();

    addLog(`✅ Processing complete! Total recordings: ${allRecordings.length}, API results: ${allApiResults.length}`);
    completeDetection(allApiResults, allRecordings);
  };

  const handleInputDeviceChange = async (deviceId) => {
    setSelectedInput(deviceId);
    
    if (microphoneStatus === 'connected') {
      cleanupAll();
      await initMicrophone();
    }
    
    addLog(`✓ Microphone changed to: ${audioInputs.find(d => d.deviceId === deviceId)?.label || 'Unknown'}`);
  };

  const completeDetection = (results, allRecordings) => {
    setIsRunning(false);
    setDetectionResults(results);
    
    if (allRecordings && allRecordings.length > 0) {
      const originalFiles = allRecordings.map(rec => ({
        fileName: rec.fileName,
        file: rec.originalFile,
        frequency: rec.frequency
      }));
      setOriginalAudioFiles(originalFiles);
    }
    
    const buzzDetectedFiles = results.filter(r => {
      return r.success && r.result?.prediction && 
             (r.result.prediction === "Buzz" || r.result.prediction === "Severe Buzz");
    });
    
    const hasBuzz = buzzDetectedFiles.length > 0;
    setHasBuzzDetected(hasBuzz);
    
    if (hasBuzz) {
      setDetectionResult('fail');
      setSystemStatus('Ready');
      addLog(`✗ Detection complete - Audio buzz detected in ${buzzDetectedFiles.length} out of ${results.length} files`);
    } else {
      setDetectionResult('pass');
      setSystemStatus('Ready');
      addLog(`✓ Detection complete - All ${results.length} files passed (No audio buzz detected)`);
    }
    cleanupAll();
  };

  const pauseDetection = () => {
    setIsPaused(true);
    setSystemStatus('Paused');
    setStatusMessage('⏸️ Processing paused...');
    
    if (playerRef.current && !playerRef.current.paused) {
      playerRef.current.pause();
      playerRef.current.currentTime = 0;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    addLog('⏸ Detection paused - current file will restart on resume');
  };

  const resumeDetection = () => {
    setIsPaused(false);
    setSystemStatus('Processing');
    startMessageRotation();
    addLog('▶ Detection resumed - restarting current file from beginning');
  };

  const stopDetection = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsRunning(false);
    setIsPaused(false);
    setCanControlRecording(false);
    setCurrentPhase('idle');

    // Reset progress
    setFilesRecorded(0);
    setFilesConverted(0);
    setFilesAnalyzed(0);
    setTotalProgress(0);

    // Clear ETA and messages
    clearETA();
    stopMessageRotation();
    setStatusMessage('');

    cleanupAll();
    addLog('⏹ Detection stopped');
  };

  useEffect(() => {
  return () => cleanupAll();
  }, []);

//mic-stop
useEffect(() => {
  if (progress === 100 && !micCleanedUpRef.current) {
    micCleanedUpRef.current = true;
    cleanupAll(); 
  }
}, [progress]);

  const cleanupAll = () => {
    if (playerRef.current) {
      playerRef.current.pause();
      playerRef.current.src = '';
      playerRef.current.srcObject = null;
      playerRef.current = null;
    }
    
     if (mediaRecorderRef.current) {
    try {
      if (['recording', 'paused'].includes(mediaRecorderRef.current.state)) {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    mediaRecorderRef.current = null;
  }
    
    if (micStreamRef.current) {
    micStreamRef.current.getTracks().forEach(track => {
      try {
        track.stop();
      } catch {}
    });
    micStreamRef.current = null;
  }
  // B. Stop the ORIGINAL raw stream (this is usually the one keeping the indicator alive!)
  if (originalMicStreamRef.current) {
    originalMicStreamRef.current.getTracks().forEach(track => {
      try {
        track.stop();
        track.enabled = false; // extra safety
      } catch {}
    });
    originalMicStreamRef.current = null;
  }
  // C. Disconnect and release all Web Audio API nodes
  if (sourceRef.current) {
    sourceRef.current.disconnect();
    sourceRef.current = null;
  }

  if (gainNodeRef.current) {
    gainNodeRef.current.disconnect();
    gainNodeRef.current = null;
  }

    if (audioContextRef.current) {
    try {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    } catch (err) {
      console.warn("[CLEANUP] AudioContext close failed (harmless):", err);
    }
    audioContextRef.current = null;
  }

  audioChunksRef.current = [];
  setMicrophoneStatus('disconnected');
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
    setLogs(prev => [...prev, { timestamp, message }].slice(-20));
  };

  const handleBackClick = () => {
    setDetectionResults([]);
    setOriginalAudioFiles([]);
    setHasBuzzDetected(false);
    setHumanJudgment(null);
    localStorage.removeItem('baseFrequency');
    setApiResults([]);
    setUploadedFiles([]);

    // Reset progress
    setFilesRecorded(0);
    setFilesConverted(0);
    setFilesAnalyzed(0);
    setTotalProgress(0);
    setTotalFiles(0);
    setCurrentPhase('idle');

    setDetectionResult('');
    setSystemStatus('Ready');
    setIsRunning(false);
    setIsPaused(false);

    clearETA();
    navigate('/audio-calibaration');
  };

  const Audiblyverify = () => {
    navigate('/audio-verification');
  };

  // const loadTestAudioFiles = async () => {
  //   setIsLoadingDefaults(true);
  //   try {
  //     addLog('📂 Loading test audio files from /test_audio folder...');

  //     const testFiles = [];
  //     const baseUrl = window.location.origin;

  //     for (let i = 1; i <= 101; i++) {
  //       const padded = String(i).padStart(3, '0');
  //       const freq = 50 + (i - 1) * 5;
  //       const fileName = `${padded}_${freq}HZ.wav`;
  //       const filePath = `${baseUrl}/test_audio/${fileName}`;

  //       try {
  //         const res = await fetch(filePath);
  //         if (!res.ok) continue;
  //         const blob = await res.blob();
  //         if (!blob.size) continue;
  //         testFiles.push(new File([blob], fileName, { type: 'audio/wav' }));
  //       } catch {
  //         // ignore individual failures
  //       }
  //     }

  //     if (!testFiles.length) {
  //       addLog('✗ No test audio files could be loaded');
  //       return;
  //     }

  //     const dt = new DataTransfer();
  //     testFiles.forEach(f => dt.items.add(f));
  //     setSyntheticFileList(dt.files);

  //     if (fileInputRef.current) {
  //       fileInputRef.current.files = dt.files;
  //     }

  //     handleFileUpload({ target: { files: dt.files } });

  //     addLog(`✓ Successfully loaded ${testFiles.length} test audio files`);
  //   } catch (error) {
  //     console.error('Error loading test files:', error);
  //     addLog(`✗ Error loading test audio files: ${error.message}`);
  //   } finally {
  //     setIsLoadingDefaults(false);
  //   }
  // };

  const loadTestAudioFiles = async () => {
    setIsLoadingDefaults(true);
    addLog('📥 Downloading default test audio files...');

    try {
      // S3 URL for the default test audio zip file
      const zipUrl = 'https://mura-ai-images-dev.s3.eu-west-1.amazonaws.com/Uploads/Template/Audio_Test.zip';

      // Download the zip file
      const response = await fetch(zipUrl);
      if (!response.ok) {
        throw new Error(`Failed to download zip file: ${response.statusText}`);
      }

      const zipBlob = await response.blob();
      addLog('✓ Zip file downloaded successfully');

      // Extract the zip file using JSZip
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(zipBlob);
      addLog('✓ Extracting audio files from zip...');

      // Convert extracted files to File objects
      const audioFiles = [];
      const filePromises = [];

      Object.keys(zipContent.files).forEach((filename) => {
        const file = zipContent.files[filename];

        // Skip directories and hidden files
        if (file.dir || filename.startsWith('__MACOSX') || filename.startsWith('.')) {
          return;
        }

        // Check if it's an audio file
        const extension = filename.split('.').pop()?.toLowerCase();
        const isAudioExtension = ['wav', 'mp3', 'ogg', 'webm', 'm4a', 'aac'].includes(extension);

        if (isAudioExtension) {
          filePromises.push(
            file.async('blob').then((blob) => {
              // Get just the filename without path
              const name = filename.split('/').pop();
              // Create a File object with proper mime type
              const mimeType = `audio/${extension === 'mp3' ? 'mpeg' : extension}`;
              return new File([blob], name, { type: mimeType });
            })
          );
        }
      });

      // Wait for all files to be extracted
      const extractedFiles = await Promise.all(filePromises);

      if (extractedFiles.length === 0) {
        throw new Error('No audio files found in the zip');
      }

      // Sort files by name
      extractedFiles.sort((a, b) => a.name.localeCompare(b.name));

      addLog(`✓ Extracted ${extractedFiles.length} audio files`);

      // Reset state
      setFilesRecorded(0);
      setFilesConverted(0);
      setFilesAnalyzed(0);
      setTotalProgress(0);
      setCurrentPhase('idle');
      setIsPaused(false);
      setIsRunning(false);
      setDetectionResult(null);
      setSystemStatus('Idle');
      setHumanJudgment(null);
      setRecordedAudioData([]);
      setApiResults([]);
      clearETA();
      setStatusMessage('');
      setCurrentFreq(null);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Set the uploaded files
      setUploadedFiles(extractedFiles);
      setLoadedViaDefaults(true);
      const dataTransfer = new DataTransfer();
      extractedFiles.forEach(file => {
        dataTransfer.items.add(file);
      });

      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
      }

      addLog(`✓ All ${extractedFiles.length} default test audio files loaded successfully`);
    } catch (error) {
      console.error('Error loading default test audio files:', error);
      addLog(`✗ Failed to load default test audio files: ${error.message}`);
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-2 font-sans">
      <div className="bg-black rounded-lg p-2">
        <div className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur-sm border-b border-gray-700">
          <div className="px-4 py-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center">
                <button
                  onClick={handleBackClick}
                  className="text-white hover:text-gray-400 transition-colors mr-2"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
                <h1 className="text-xl font-bold text-white">
                  Audio Buzz Detection
                </h1>
              </div>

              <div className="flex items-center gap-3 md:gap-3">
                <button
                  onClick={() => setShowSetupModal(true)}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors border border-gray-600"
                >
                  Setup
                </button>

                <button
                  onClick={() => setShowConfigModal(true)}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors border border-green-500"
                >
                  <Settings className="w-5 h-5" />
                  Configuration
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Modal */}
        {showSetupModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-lg p-6 max-w-4xl w-full border border-gray-700 mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Analysis Parameters</h2>
                <button 
                  onClick={() => setShowSetupModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Strong Threshold (dB)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={strongThreshold}
                    onChange={(e) => setStrongThreshold(Number(e.target.value))}
                    className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Std Threshold 1</label>
                  <input
                    type="number"
                    step="0.1"
                    value={stdThreshold1}
                    onChange={(e) => setStdThreshold1(Number(e.target.value))}
                    className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Std Threshold 2</label>
                  <input
                    type="number"
                    step="0.1"
                    value={stdThreshold2}
                    onChange={(e) => setStdThreshold2(Number(e.target.value))}
                    className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Outlier Count Threshold</label>
                  <input
                    type="number"
                    value={outlierCountThreshold}
                    onChange={(e) => setOutlierCountThreshold(Number(e.target.value))}
                    className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white"
                  />
                </div>
              </div>

              <button
                onClick={() => setShowSetupModal(false)}
                className="w-full mt-6 bg-green-600 hover:bg-green-700 py-3 rounded-lg font-semibold transition-colors"
              >
                Save Parameters
              </button>
            </div>
          </div>
        )}

        {/* Configuration Modal */}
        {showConfigModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-lg p-2 max-w-2xl w-full border border-gray-700 mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">System Configuration</h2>
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Audio Devices</h3>
                <div className="space-y-4">
                  {/* ─── MICROPHONE SELECTOR ─────────────────────────────────────── */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Microphone (System/External)
                    </label>

                    {/* Mobile: Custom dropdown */}
                    <div className="block sm:hidden">
                      <div ref={inputRef} className="relative w-full">
                        <div
                          className="flex items-center justify-between gap-2 p-2 bg-gray-800 border border-gray-600 rounded text-white cursor-pointer break-words max-h-[60px] overflow-hidden"
                          onClick={() => setInputOpen(!inputOpen)}
                        >
                          <span className="flex-1 break-words">
                            {audioInputs.find((device) => device.deviceId === selectedInput)?.label ||
                              'Select Microphone'}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-200 font-bold transition-transform duration-200 ${
                              inputOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </div>

                        {inputOpen && (
                          <div className="absolute left-0 z-10 w-full bg-gray-800 border border-gray-600 rounded mt-1 max-h-40 overflow-y-auto">
                            {audioInputs.map((device, index) => {
                              const isSelected = device.deviceId === selectedInput;
                              return (
                                <div
                                  key={device.deviceId}
                                  className={`p-2 cursor-pointer whitespace-normal break-words border-b border-gray-600 last:border-b-0 ${
                                    isSelected ? 'bg-gray-500 text-white' : 'hover:bg-gray-700'
                                  }`}
                                  onClick={() => {
                                    handleInputDeviceChange(device.deviceId);
                                    setInputOpen(false);
                                  }}
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      handleInputDeviceChange(device.deviceId);
                                      setInputOpen(false);
                                    }
                                  }}
                                >
                                  {device.label || `Microphone ${index + 1}`}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Desktop: Native <select> */}
                    <div className="hidden sm:block">
                      <select
                        value={selectedInput}
                        onChange={(e) => handleInputDeviceChange(e.target.value)}
                        className="w-full p-2 bg-gray-800 border border-gray-600 rounded text-white"
                      >
                        {audioInputs.map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="border-t border-gray-600 pt-4">
                    <label className="block text-sm font-medium mb-2">Speaker Output</label>

                    {/* Mobile: Custom dropdown */}
                    <div className="block sm:hidden">
                      <div ref={outputRef} className="relative w-full">
                        <div
                          className="flex items-center justify-between gap-2 p-2 bg-gray-800 border border-gray-600 rounded text-white cursor-pointer break-words max-h-[60px] overflow-hidden"
                          onClick={() => setOutputOpen(!outputOpen)}
                        >
                          <span className="flex-1 break-words">
                            {audioOutputs.find((device) => device.deviceId === selectedOutput)?.label ||
                              'Select Speaker'}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-200 font-bold transition-transform duration-200 ${
                              outputOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </div>

                        {outputOpen && (
                          <div className="absolute left-0 z-10 w-full bg-gray-800 border border-gray-600 rounded mt-1 max-h-40 overflow-y-auto mb-2 pb-3">
                            {audioOutputs.map((device, index) => {
                              const isSelected = device.deviceId === selectedOutput;
                              return (
                                <div
                                  key={device.deviceId}
                                  className={`p-2 cursor-pointer whitespace-normal break-words border-b border-gray-600 last:border-b-0 ${
                                    isSelected ? 'bg-gray-500 text-white' : 'hover:bg-gray-700'
                                  }`}
                                  onClick={() => {
                                    setSelectedOutput(device.deviceId);
                                    setOutputOpen(false);
                                  }}
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      setSelectedOutput(device.deviceId);
                                      setOutputOpen(false);
                                    }
                                  }}
                                >
                                  {device.label || `Speaker ${index + 1}`}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Desktop: Native <select> */}
                    <div className="hidden sm:block">
                      <select
                        value={selectedOutput}
                        onChange={(e) => setSelectedOutput(e.target.value)}
                        className="w-full p-2 bg-gray-800 border border-gray-600 rounded text-white"
                      >
                        {audioOutputs.map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Speaker ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowConfigModal(false)}
                className="w-full bg-green-600 hover:bg-green-700 py-2 rounded-lg font-semibold mt-6"
              >
                Save Configuration
              </button>
            </div>
          </div>
        )}
        <div className="bg-black rounded-lg p-3 mt-4 mb-4 border border-slate-700">
          <div className="flex flex-col gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              multiple
              onChange={(e) => {
                setLoadedViaDefaults(false);
                setSyntheticFileList(null);
                handleFileUpload(e);
              }}
              disabled={isRunning || isLoadingDefaults}
              className="text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700 disabled:opacity-50"
            />

            {uploadedFiles.length > 0 && (
              <span className={`text-sm font-medium ${loadedViaDefaults ? 'text-blue-400' : 'text-green-400'}`}>
               ✓ {uploadedFiles.length} {loadedViaDefaults ? 'default test files' : 'files'} loaded
              </span>
            )}
          </div>
        </div>

        <button
          onClick={loadTestAudioFiles}
          disabled={isRunning || isLoadingDefaults}
          className="bg-blue-700 hover:bg-gray-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-semibold transition-colors border border-gray-600 flex items-center gap-2"
        >
          Load default test audio files
        </button>

        {isLoadingDefaults && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-green-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-100">
                Loading default test audio files...
              </p>
            </div>
          </div>
        )}

        <div className="bg-black mt-6 border border-slate-700 rounded-lg pr-1 pl-1 sm:mb-10">
          <div className="flex justify-between items-center mb-2 pt-4">
            <h2 className="text-lg font-semibold pl-6">Audio Buzz Detection Progress</h2>
            <span className="text-green-500 pr-6 text-xl font-bold flex items-center gap-2">
              {Math.round(totalProgress)}%
              {isPaused && <span className="text-yellow-400 text-sm font-semibold">(Paused)</span>}
            </span>
          </div>
          
          <div className="px-6"> 
            <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
              <div
                className="bg-green-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${totalProgress}%` }}
              ></div>
            </div>
          </div>
          
          {/* ✅ ETA and Status Message Display */}
          {isRunning && (
            <div className="px-6 mb-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">ETA:</span>
                  <span className="text-lg text-blue-400 font-bold">{formatTime(etaSeconds)}</span>
                </div>
                <div className="text-sm text-green-400 italic min-h-[20px] transition-opacity duration-300">
                  {statusMessage}
                </div>
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-300 pt-3 mb-15 pl-6">
            Please wait while the audio buzz detection process is ongoing. Ensure the Device Under Test (DUT) is properly set up.
          </p>
          
          <div className="border-t border-slate-700 w-[97%] mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-5 mb-10">
              <div className="flex flex-row md:flex-row justify-between items-start gap-6 pb-3 mb-4">
                {/* ✅ Basic Frequency Display - Now Dynamic */}
               <div className="flex flex-col">
                <span className="text-sm text-gray-300 mb-1">Basic Frequency</span>
                <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-white">
                {currentFreq !== null ? currentFreq : '—'}
                </span>
                <span className="text-sm text-gray-300">Hz</span>
                 {isRunning && currentFreq !== null && (
                <RotateCw className="w-5 h-5 text-gray-400 animate-spin" />
                 )}
             </div>
             </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-gray-300">Detection Result</span>
                  <div className="flex items-center gap-2 text-white-500 mt-1">
                    {detectionResult === 'pass' ? (
                      <Check className="w-7 h-7 text-green-500" />
                    ) : detectionResult === 'fail' ? (
                      <XCircle className="w-7 h-7 text-red-500" />
                    ) : (
                      <RotateCcw className="w-7 h-7 text-gray-400" />
                    )}
                    <span className={`text-2xl font-semibold ${
                      detectionResult === 'pass' ? 'text-green-500' : 
                      detectionResult === 'fail' ? 'text-red-500' : 
                      'text-gray-400'
                    }`}>
                      {detectionResult === 'pass' ? 'Pass' : 
                       detectionResult === 'fail' ? 'Fail' : 
                       'Pending'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                {!isRunning ? (
                  <button 
                    onClick={startDetection}
                    disabled={uploadedFiles.length === 0}
                    className="w-full md:w-auto bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded px-6 py-2 md:px-8 md:py-3 text-center font-semibold transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <RotateCcw className="w-5 h-5" />
                    Audio Buzz Detection
                  </button>
                ) : (
                  <>
                    {!isPaused ? (
                      <button
                        onClick={pauseDetection}
                        disabled={!canControlRecording}
                        className={`w-full md:w-auto rounded px-6 py-2 md:px-8 md:py-3 text-center transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${!canControlRecording
                          ? 'bg-gray-600 cursor-not-allowed border border-gray-500'
                          : 'bg-gray-600 hover:bg-gray-500 border border-gray-700'
                          }`}
                      >
                        <Pause className="w-5 h-5 fill-current" />
                        Pause
                      </button>
                    ) : (
                      <button 
                        onClick={resumeDetection}
                        disabled={!canControlRecording}
                        className={`w-full md:w-auto rounded px-6 py-2 md:px-8 md:py-3 text-center transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
                          !canControlRecording
                            ? 'bg-gray-600 cursor-not-allowed'
                            : 'bg-green-500 hover:bg-green-600'
                        }`}
                      >
                        <RotateCcw className="w-5 h-5" />
                        Resume
                      </button>
                    )}
                    <button 
                      onClick={stopDetection}
                      disabled={!canControlRecording}
                      className={`w-full md:w-auto rounded px-6 py-2 md:px-8 md:py-3 text-center transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
                        !canControlRecording
                          ? 'bg-gray-700 cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-500'
                      }`}
                    >
                      <Square className="w-5 h-5 fill-current" />
                      Stop
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rest of your component remains the same - Human Judgment, AI Detection Result sections, etc. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 sm:gap-25 gap-5 mb-10">
        {/* Human Judgment section */}
        <div className="bg-black rounded-lg p-8 w-full max-w-3xl mx-auto min-h-[220px] border border-slate-700">
          <h3 className="font-semibold text-lg mb-5">Human Judgment</h3>
          <p className="text-sm text-gray-300 mb-8">
            Please verify the system judgment result audibly and confirm your finding.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => handleHumanJudgment('no_buzz')}
              disabled={totalProgress < 100}
              className={`flex-1 rounded px-5 py-2 md:px-5 md:py-3
                flex items-center justify-center gap-2
                text-sm font-semibold transition-colors
                ${totalProgress < 100
                  ? 'bg-gray-600 border-2 border-gray-400 text-gray-300 cursor-not-allowed'
                  : humanJudgment === 'no_buzz'
                    ? 'bg-green-500 hover:bg-green-700 text-white border-2 border-green-400'
                    : 'bg-gray-700 hover:bg-gray-600 text-green-500 border-2 border-green-400'
                }`}
            >
              <Check className="w-6 h-6" />
              No Buzz
            </button>
            <button
              onClick={() => handleHumanJudgment('buzz')}
              disabled={totalProgress < 100}
              className={`flex-1 rounded px-5 py-2 md:px-5 md:py-3
                flex items-center justify-center gap-2
                text-sm font-semibold transition-colors
                ${totalProgress < 100
                  ? 'bg-gray-600 border-2 border-gray-400 text-gray-300 cursor-not-allowed'
                  : humanJudgment === 'buzz'
                    ? 'bg-red-500 hover:bg-red-700 text-white border-2 border-red-500'
                    : 'bg-gray-700 hover:bg-gray-600 text-white border-2 border-red-400'
                }`}
            >
              <AlertCircle className="w-6 h-6" />
              Audio Buzz
            </button>
          </div>
        </div>

        {/* AI Detection Result section */}
        <div className="bg-black rounded-lg p-8 w-full max-w-3xl mx-auto min-h-[220px] border border-slate-700">
          <h3 className="font-semibold mb-5 text-lg">AI Detection Result</h3>

          {totalProgress < 100 && (
            <>
              <div className="flex items-center mb-3 gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center">
                  <Check className="w-6 h-6 text-gray-400" />
                </div>
                <span className="text-gray-400">Processing audio files…</span>
              </div>
              <p className="text-sm text-gray-300">
                AI analysis results will appear once processing is complete.
              </p>
            </>
          )}

          {totalProgress === 100 && (() => {
            const buzzFiles = apiResults.filter(item => {
              const prediction = item.result?.prediction;
              return (
                item.success &&
                prediction &&
                (prediction === "Buzz" || prediction === "Severe Buzz")
              );
            });

            if (buzzFiles.length > 0) {
              const frequencies = buzzFiles
                .map(item => item.result.base_frequency)
                .filter(Boolean);

              const previewFrequencies = frequencies.slice(0, 3);

              return (
                <>
                  <div className="flex items-center mb-3 gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-white">Audio Buzz Detected</span>
                  </div>

                  <p className="text-sm text-gray-300">
                    The AI detection has identified an audio buzz in the basic frequency audio, including{" "}
                    <span className="text-red-400 font-semibold">
                      {previewFrequencies.join(" Hz, ")} Hz
                    </span>

                    {frequencies.length > 3 && !showAllBuzz && (
                      <>
                        {", "}
                        <button
                          onClick={() => setShowAllBuzz(true)}
                          className="text-blue-400 underline font-semibold ml-1"
                        >
                          and more
                        </button>
                      </>
                    )}
                    . Please verify this result using the Audibly Verify the System Result function.
                  </p>

                  {showAllBuzz && (() => {
                    const remainingFrequencies = frequencies.slice(3);

                    if (remainingFrequencies.length === 0) return null;

                    return (
                      <div className="mt-4 bg-gray-900 border border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-white">
                            Detected Buzz Frequencies
                          </span>

                          <button
                            onClick={() => setShowAllBuzz(false)}
                            className="text-gray-400 hover:text-red-400 transition"
                            title="Close"
                          >
                            ✕
                          </button>
                        </div>

                        <div className="mt-2 text-sm text-gray-300 flex flex-wrap gap-x-1">
                          {remainingFrequencies.map((freq, idx) => (
                            <span key={idx}>
                              <span className="text-red-400 font-semibold">
                                {freq} Hz
                              </span>
                              {idx < remainingFrequencies.length - 1 && ", "}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </>
              );
            }
            
            return (
              <>
                <div className="flex items-center mb-3 gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center">
                    <Check className="w-6 h-6 text-[#10b981]" />
                  </div>
                  <span className="text-gray-400">No Audio Buzz found by the AI detection.</span>
                </div>
                <p className="text-sm text-gray-300">
                  The AI detection did not find any audio buzz. You can proceed or manually verify the result.
                </p>
              </>
            );
          })()}
        </div>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 z-50"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="w-full bg-black">
          <div className="mx-auto w-full px-4 py-1 sm:px-5 sm:py-3">
            <button
              className={`w-full rounded-lg border-2 py-2 flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus:ring-2 ${totalProgress < 100
                ? 'bg-gray-600 border-gray-400 text-gray-300 cursor-not-allowed'
                : 'bg-green-500 border-green-400 text-white hover:bg-green-600'
                }`}
              onClick={Audiblyverify}
              type="button"
              disabled={totalProgress < 100}
            >
              <Volume2 className="w-5 h-5 sm:w-6 sm:h-6 mt-1" />
              <span className="font-semibold text-sm sm:text-lg">
                Audibly Verify The System Result
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioBuzzDetection;


